import { useRef, useState } from "react";
import { MoveHorizontal, ArrowRight } from "lucide-react";
import { requestOffer } from "./scroll";
import { BEFORE_AFTER } from "./data";
import { Reveal } from "./Reveal";

const Slider = ({ pair }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);
  const update = (x) => {
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(100, Math.max(0, ((x - r.left) / r.width) * 100)));
  };
  const tag = "absolute top-4 z-10 bg-ink/80 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-white backdrop-blur";

  return (
    <div
      ref={ref}
      className="relative aspect-[4/3] w-full cursor-ew-resize touch-pan-y select-none overflow-hidden bg-ink sm:aspect-[3/2] lg:aspect-[16/9]"
      onPointerDown={(e) => { dragging.current = true; e.currentTarget.setPointerCapture(e.pointerId); update(e.clientX); }}
      onPointerMove={(e) => dragging.current && update(e.clientX)}
      onPointerUp={() => { dragging.current = false; }}
      onPointerCancel={() => { dragging.current = false; }}
      data-testid="before-after-slider"
    >
      <img src={pair.after} alt={`Nachher: ${pair.title}`} loading="lazy" draggable="false" className="absolute inset-0 h-full w-full object-cover" />
      <img src={pair.before} alt={`Vorher: ${pair.title}`} loading="lazy" draggable="false" className="absolute inset-0 h-full w-full object-cover" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} />
      <span className={`${tag} left-4`}>Vorher</span>
      <span className={`${tag} right-4 !bg-terra-deep`}>Nachher</span>
      <div className="pointer-events-none absolute inset-y-0 z-10 w-0.5 bg-white" style={{ left: `${pos}%` }}>
        <span className="absolute left-1/2 top-1/2 grid h-14 w-14 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white text-ink shadow-2xl">
          <MoveHorizontal size={22} />
        </span>
      </div>
      <input type="range" min="0" max="100" value={Math.round(pos)} onChange={(e) => setPos(Number(e.target.value))}
        aria-label="Vorher-Nachher-Vergleich verschieben" className="sr-only" data-testid="before-after-range" />
    </div>
  );
};

export const BeforeAfter = () => {
  const [active, setActive] = useState(0);
  const pair = BEFORE_AFTER[active];
  return (
    <div className="mt-24 sm:mt-32" data-testid="before-after-section">
      <Reveal className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="eyebrow text-terra">Vorher / Nachher</p>
          <h3 className="mt-4 font-display text-4xl font-bold uppercase leading-[0.95] text-white sm:text-5xl">Der Unterschied<br />liegt im Detail.</h3>
        </div>
        <div className="flex gap-2" role="tablist" aria-label="Projekt wählen">
          {BEFORE_AFTER.map((p, i) => (
            <button key={p.id} type="button" role="tab" aria-selected={active === i} onClick={() => setActive(i)}
              className={`rounded-full border px-5 py-2.5 text-xs font-bold uppercase tracking-[0.18em] transition-colors duration-300 ${active === i ? "border-terra bg-terra-deep text-white" : "border-white/20 text-slate-200 hover:border-white/50"}`}
              data-testid={`before-after-tab-${p.id}`}>
              {p.label}
            </button>
          ))}
        </div>
      </Reveal>
      <Reveal delay={0.1} className="mt-10">
        <Slider key={pair.id} pair={pair} />
        <div className="mt-5 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <p className="text-sm text-slate-300">{pair.title}<span className="mt-1 block text-xs text-slate-400">Visualisierung / Beispielprojekt · Regler ziehen zum Vergleichen</span></p>
          <button type="button" onClick={() => requestOffer("Dachsanierung & Dämmung")} className="btn-primary btn-mega justify-center" data-testid="before-after-cta">
            So soll mein Dach aussehen <ArrowRight size={18} className="btn-arrow" />
          </button>
        </div>
      </Reveal>
    </div>
  );
};
